import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalMe, useUpdateMe } from '@/api/hooks/usePortal';

export function PortalProfilePage() {
  const { data: me, isLoading } = usePortalMe();
  const updateMe = useUpdateMe();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (me) {
      setFirstName(me.first_name ?? '');
      setLastName(me.last_name ?? '');
      setPhone(me.phone_number ?? '');
      setJobTitle(me.job_title ?? '');
    }
  }, [me]);

  const isDirty = me && (
    firstName !== (me.first_name ?? '') ||
    lastName !== (me.last_name ?? '') ||
    phone !== (me.phone_number ?? '') ||
    jobTitle !== (me.job_title ?? '')
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) { setError('First name is required'); return; }
    if (!lastName.trim()) { setError('Last name is required'); return; }
    try {
      await updateMe.mutateAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
      });
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to save changes');
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div className="flex items-center gap-2">
        <User className="h-5 w-5 text-text-secondary" />
        <h1 className="text-lg font-bold text-text-primary">Profile</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="first_name">First name</Label>
            <Input
              id="first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name">Last name</Label>
            <Input
              id="last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={me?.email ?? ''} disabled className="opacity-60" />
          <p className="text-xs text-text-secondary">Contact your administrator to change your email address.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="job_title">Job title</Label>
          <Input
            id="job_title"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Head of Information Security"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone_number">Phone number</Label>
          <Input
            id="phone_number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 000 0000"
            autoComplete="tel"
          />
        </div>

        {error && <p className="text-sm text-accent-rose">{error}</p>}

        <Button type="submit" disabled={!isDirty || updateMe.isPending}>
          {updateMe.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </div>
  );
}
